import { SeriesTag, SessionTag } from './types'

export const MANY_UNAUDITED_SESSIONS_THRESHOLD = 9
export const LONG_ONE_ON_ONE_TIME_MINUTES = 120
export const LONG_ONE_ON_ONE_TIME_SEC = 60 * LONG_ONE_ON_ONE_TIME_MINUTES
export const FRESH_ONE_ON_ONE_CHECK_COUNT = 3

export const SESSION_TAGS = Object.values(SessionTag)

export const SERIES_TAGS = Object.values(SeriesTag)

export const AUDITOR_TAG_LABELS: Partial<Record<SessionTag, string>> = {
  [SessionTag.NotHelpfulRatings]: 'Not Helpful Ratings',
  [SessionTag.ManyUnauditedSessions]: 'Many Unaudited Sessions',
  [SessionTag.FirstTwoSessions]: 'First Two Sessions',
  [SessionTag.LowNPSSeries]: 'In a Low NPS Series',
}

export const AUDITOR_TAGS = Object.keys(AUDITOR_TAG_LABELS) as (keyof typeof AUDITOR_TAG_LABELS)[]

export const MENTOR_TAGS = [SessionTag.EscalatedToMentor, SessionTag.LowRating]

export const SAFETY_TAGS = [
  SessionTag.LongOneOnOne,
  SessionTag.LowEngagement,
  SessionTag.AIRiskHigh,
]

export const AUTO_GENERATED_SESSION_TAGS = [
  SessionTag.NotHelpfulRatings,
  SessionTag.LongOneOnOne,
  SessionTag.ManyUnauditedSessions,
  SessionTag.FirstTwoSessions,
  SessionTag.TutorNoShow,
  SessionTag.EscalatedToMentor,
  SessionTag.LowRating,
  SessionTag.LowNPSSeries,
  SessionTag.LowEngagement,
  SessionTag.HighlyEngagingSession,
  SessionTag.AIRiskHigh,
] as const

export const AUTO_GENERATED_BREAKOUT_SESSION_TAGS = [
  SessionTag.LongOneOnOne,
  SessionTag.AIRiskHigh,
] as const
export type AutoGeneratedSessionTag = (typeof AUTO_GENERATED_SESSION_TAGS)[number]
export type AutoGeneratedBreakoutSessionTag = (typeof AUTO_GENERATED_BREAKOUT_SESSION_TAGS)[number]

export const AUTO_GENERATED_SERIES_TAGS = [
  SeriesTag.HighlyEngagingSATBootcamp,
  SeriesTag.HighlyEngagingCollegeAdmissionsWorkshop,
  SeriesTag.HighlyEngagingAlgebra2LearningLab,
  SeriesTag.HighlyEngagingPreCalculusLearningLab,
] as const

export type AutoGeneratedSeriesTag = (typeof AUTO_GENERATED_SERIES_TAGS)[number]

export const SUMMER_CAMP_TAGS = [SessionTag.SummerCamp23] as const

export const SUMMER_CAMP_2022_TAGS = [
  SessionTag.SummerCamp22Arts,
  SessionTag.SummerCamp22Writing,
  SessionTag.SummerCamp22Humanities,
  SessionTag.SummerCamp22STEM,
  SessionTag.SummerCamp22JustForFun,
  SessionTag.SummerCamp22Community,
] as const

export const MANUALLY_ASSIGNED_TAGS = [
  SessionTag.NotAskingQuestions,
  SessionTag.PersonalizedGuidance,
  SessionTag.RelationshipBuilding,
  SessionTag.FunEngaging,
  SessionTag.LeveragedTech,
  SessionTag.GreatExplanations,
  SessionTag.TutorOfMonth,
  SessionTag.SummerCamp22Arts,
  SessionTag.SummerCamp22Writing,
  SessionTag.SummerCamp22Humanities,
  SessionTag.SummerCamp22STEM,
  SessionTag.SummerCamp22JustForFun,
  SessionTag.SummerCamp22Featured,
  SessionTag.SummerCamp22Community,
  SessionTag.KhanAcademyCrashCourseSummer22,
  SessionTag.SummerCamp23,
  SessionTag.SummerCamp23Featured,
  SessionTag.SummerCamp2024Featured,
] as const

export type ManuallyAssignedSessionTag = (typeof MANUALLY_ASSIGNED_TAGS)[number]

export const MANUALLY_ASSIGNED_SERIES_TAGS = [
  SeriesTag.SummerCamp2025Featured,
  SeriesTag.FeaturedCommunityEvent,
] as const

export type ManuallyAssignedSeriesTag = (typeof MANUALLY_ASSIGNED_SERIES_TAGS)[number]

export const SESSION_TAG_TO_DESCRIPTION: Partial<Record<SessionTag, string>> = {
  [SessionTag.NotHelpfulRatings]:
    'The session received 2+ Not Helpful ratings, or has < 4 learners and 1+ Not Helpful.',
  [SessionTag.FirstTwoSessions]: "This is the tutor's first or second session.",
  [SessionTag.ManyUnauditedSessions]: `It has been at least ${MANY_UNAUDITED_SESSIONS_THRESHOLD} sessions since this tutor's last peer review.`,
  [SessionTag.TutorNoShow]: 'Tutor might not have showed up to the session.',
  [SessionTag.LongOneOnOne]: 'This 1:1 session was too long.',
  [SessionTag.LowEngagement]: 'This session has low engagement.',
  [SessionTag.RepeatedOneOnOne]: 'This 1:1 pairing has met up several times.',
  [SessionTag.EscalatedToMentor]: 'An auditor escalated this session to the coach team ',
  [SessionTag.LowRating]:
    'A peer review has a No for either Respect, Mastery, or 2+ Active Learning questions.',
  [SessionTag.LowNPSSeries]: 'This session is in a series with a low NPS rating',
  [SessionTag.AIRiskHigh]: 'AI analysis flagged this session as high risk.',
}

export const SESSION_TAG_TO_LABEL: Record<SessionTag, string> = {
  [SessionTag.NotHelpfulRatings]: 'Not Helpful Ratings',
  [SessionTag.FirstTwoSessions]: 'First Two Sessions',
  [SessionTag.ManyUnauditedSessions]: 'Many Unaudited Sessions',
  [SessionTag.TutorNoShow]: 'Tutor No Show',
  [SessionTag.LongOneOnOne]: 'Long 1:1 Session',
  [SessionTag.LowEngagement]: 'Low Engagement',
  [SessionTag.RepeatedOneOnOne]: 'Repeated 1:1 Session',
  [SessionTag.EscalatedToMentor]: 'Escalated to Coach',
  [SessionTag.LowRating]: 'Low Ratings',
  [SessionTag.LowNPSSeries]: 'In Low NPS Series',
  [SessionTag.NotAskingQuestions]: 'Need Help: Not Asking Learners Questions',
  [SessionTag.PersonalizedGuidance]: 'Good Practice: found and filled knowledge gaps',
  [SessionTag.RelationshipBuilding]: 'Good Practice: built relationships and trust with learners',
  [SessionTag.FunEngaging]: 'Good Practice: created an engaging environment',
  [SessionTag.LeveragedTech]: 'Good Practice: creatively leveraged technology',
  [SessionTag.GreatExplanations]: 'Good Practice: provided great explanations',
  [SessionTag.TutorOfMonth]: 'Tutor of the month nomination',
  [SessionTag.AskQuestions]: 'Good Practice: invited learners to ask questions',
  [SessionTag.PracticeProblems]: 'Good Practice: provided practice problems',
  [SessionTag.SummerCamp22Arts]: 'Summer Camp 2022 Arts',
  [SessionTag.SummerCamp22Writing]: 'Summer Camp 2022 Writing',
  [SessionTag.SummerCamp22Humanities]: 'Summer Camp 2022 Humanities',
  [SessionTag.SummerCamp22STEM]: 'Summer Camp 2022 STEM',
  [SessionTag.SummerCamp22JustForFun]: 'Summer Camp 2022 Just for fun',
  [SessionTag.SummerCamp22Community]: 'Summer Camp 2022 Community',
  [SessionTag.SummerCamp22Featured]: 'Summer Camp 2022 Featured',
  [SessionTag.KhanAcademyCrashCourseSummer22]: 'Khan Academy Crash Course - Summer 2022',
  [SessionTag.SummerCamp23]: 'Summer Camp 2023',
  [SessionTag.SummerCamp23Featured]: 'Summer Camp 2022 Featured',
  [SessionTag.SummerCamp2024]: 'Summer Camp 2024',
  [SessionTag.SummerCamp2024Featured]: 'Summer Camp 2024 Featured',
  [SessionTag.HighlyEngagingSession]: 'Highly Engaging Session',
  [SessionTag.AIRiskHigh]: 'High Risk Session',
}

export const SERIES_TAG_TO_LABEL: Record<SeriesTag, string> = {
  [SeriesTag.HighlyEngagingSATBootcamp]: 'Highly Engaging Bootcamp',
  [SeriesTag.SummerCamp2024]: 'Summer Camp 2024',
  [SeriesTag.SummerCamp2024Featured]: 'Summer Camp 2024 Featured',
  [SeriesTag.SummerCamp2025]: 'Summer Camp 2025',
  [SeriesTag.SummerCamp2025Featured]: 'Summer Camp 2025 Featured',
  [SeriesTag.FeaturedCommunityEvent]: 'Featured Community Event',
  [SeriesTag.HighlyEngagingCollegeAdmissionsWorkshop]: 'Highly Engaging CAW',
  [SeriesTag.HighlyEngagingAlgebra2LearningLab]: 'Highly Engaging Algebra 2 Learning Lab',
  [SeriesTag.HighlyEngagingPreCalculusLearningLab]: 'Highly Engaging Pre-Calculus Learning Lab',
  [SeriesTag.ExperimentAIGuideControl]: 'AI Guide Control',
  [SeriesTag.ExperimentAIGuideExperiment]: 'AI Guide Experiment',
}
